import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { Badge, type BadgeVariant } from '~/components/ui/Badge';
import { PageTitle } from '~/components/ui/Page';
import { Table } from '~/components/ui/Table';
import { useStudentFocus } from '~/modules/studentFocus';
import buttonStyles from '~/styles/button.module.css';

type CertificateSummary = components['schemas']['CertificateSummary'];

type CertificatesSearch = {
  user_id?: number;
};

export const Route = createFileRoute('/_admin/certificates/')({
  component: CertificatesList,
  validateSearch: (search: Record<string, unknown>): CertificatesSearch => {
    const raw = search.user_id;
    const id =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && raw !== ''
          ? Number(raw)
          : undefined;
    return {
      user_id: id !== undefined && Number.isFinite(id) ? id : undefined,
    };
  },
});

const columnHelper = createColumnHelper<CertificateSummary>();

function typeBadge(type: string): { label: string; variant: BadgeVariant } {
  return type === 'series'
    ? { label: 'Series', variant: 'warning' }
    : { label: 'Course', variant: 'neutral' };
}

const columns = [
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('user_display_name', {
    header: 'Student',
    cell: (info) => (
      <Link
        to="/students/$studentId"
        params={{ studentId: String(info.row.original.user_id) }}
        style={{ color: 'var(--blue)', textDecoration: 'none' }}
      >
        {info.getValue()}
      </Link>
    ),
  }),
  columnHelper.accessor('template_title', { header: 'Certificate' }),
  columnHelper.accessor('type', {
    header: 'Type',
    cell: (info) => {
      const { label, variant } = typeBadge(info.getValue());
      return <Badge $variant={variant}>{label}</Badge>;
    },
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: (info) =>
      info.row.original.is_pending ? (
        <Badge $variant="warning">Pending</Badge>
      ) : (
        <Badge $variant="success">Awarded</Badge>
      ),
  }),
  columnHelper.accessor('awarded_at', {
    header: 'Awarded',
    cell: (info) => {
      const v = info.getValue();
      return v ? new Date(v).toLocaleString() : '—';
    },
  }),
];

function CertificatesList() {
  const search = Route.useSearch();
  const [page, setPage] = useState(0);
  const { focusedStudent } = useStudentFocus();

  const effectiveUserId = search.user_id ?? focusedStudent?.id;

  const { data, isLoading, error } = api.listCertificates.useQuery({
    query: {
      limit: 10,
      offset: 10 * page,
      ...(effectiveUserId !== undefined ? { user_id: effectiveUserId } : {}),
    },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <PageTitle>Certificates</PageTitle>

      {search.user_id !== undefined ? (
        <FilterBanner>
          <span>
            Filtered to certificates for student{' '}
            <Link
              to="/students/$studentId"
              params={{ studentId: String(search.user_id) }}
            >
              #{search.user_id}
            </Link>
          </span>
          <Link to="/certificates" search={{}}>
            Clear filter
          </Link>
        </FilterBanner>
      ) : null}

      {isLoading ? (
        <LoadingMessage>Loading Certificates...</LoadingMessage>
      ) : error ? (
        <ErrorMessage>Error loading Certificates</ErrorMessage>
      ) : (
        <>
          <Toolbar>
            <div style={{ flexGrow: 1 }}>Page {page + 1}</div>
            <Button
              disabled={page === 0}
              className={buttonStyles.Button}
              onClick={() => setPage((v) => v - 1)}
            >
              Previous
            </Button>
            <Button
              className={buttonStyles.Button}
              onClick={() => setPage((v) => v + 1)}
            >
              Next
            </Button>
          </Toolbar>

          <Table table={table} />
        </>
      )}
    </div>
  );
}

const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  margin: var(--component-margin);
  gap: var(--component-margin);
`;

const FilterBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.625rem 0.875rem;
  margin: var(--component-margin);
  background: color-mix(in srgb, var(--blue) 8%, transparent);
  border-left: 3px solid var(--blue);
  border-radius: 4px;

  a {
    color: var(--blue);
  }
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--grey-600);
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--error);
`;
